import React, { useMemo, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi, RCCTooltip, RCCSelectOptionsCard } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import Loader from "src/components/Loader";
import LovSectors from "src/components/Lov/Sectors";
import LovUsers from "src/components/Lov/Users";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import { standardErrorMessages } from "src/utils/errors";
import LovMaps from "src/components/Lov/Maps";
import formatDate from "src/utils/formatDate";
import { IRefActionsPanelModalAddChangeDelete } from "src/pages/Actions/Panel/interface";
import ModalAddChangeDelete from "src/pages/Actions/Panel/components/modalAddChangeDelete";

import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import LovProgram from "src/components/Lov/Program";
import LovProject from "src/components/Lov/Projects";
import ButtonExpandGroupers from "src/components/ButtonExpandGroupers";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { useParams , useLocation } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import LovPerspectives from "src/components/Lov/Perspectives";
import LovObjectives from "src/components/Lov/Objectives";
import { IReorderRef } from "src/components/ModalReorder/interface";
import ModalReorder from "src/components/ModalReorder";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import Reports from "src/components/Reports";
import ReportGroupers from "src/components/Reports/interface";
import ModalAddChangeDeleteKanban from "src/pages/Actions/Kanban/kanbanModes/MultipleKanbans/modals";
import { IRefKanbanModal } from "src/pages/Actions/Kanban/kanbanModes/MultipleKanbans/interface";
import { v4 } from "uuid";
import { IProgram, IRefProgramModals } from "../Program/interface";
import ModalProgram from "../Program/index";
import ModalProject from "../Project/index";
import ModalSprint from "../Sprint/index";
import ModalDetails, { IProjectDetailsRef } from "./modalDetails";
import ModalReleaseActions, { IProjectReleaseActionsRef } from "./modalReleaseActions";
import { IProject, IRefProjectModals } from "../Project/interface";
import ListProgram from "./listProgram";
import { IFiltersProjectPanel, ProjectSituation } from "./interface";
import ContainerAccordion from "./style";
import { IRefSprintModals } from "../Sprint/interface";
import ModalGantt from "./Components/ModalGantt";
import { IProjectModalGanttRef } from "./Components/ModalGantt/interface";

const ProjectsPanel: React.FC = () => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { user } = useAuth();

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const params = useParams<{
        projectId: string;
    }>();

	const refModalsProgram = useRef<IRefProgramModals>(null);
	const refModalsProject = useRef<IRefProjectModals>(null);
	const refModalsSprint = useRef<IRefSprintModals>(null);
	const refModalsDetails = useRef<IProjectDetailsRef>(null);
	const refModalReleaseActions = useRef<IProjectReleaseActionsRef>(null);
	const refModalsActions = useRef<IRefActionsPanelModalAddChangeDelete>(null);
	const refModalKanban = useRef<IRefKanbanModal>(null);
	const modalReorderRef = useRef<IReorderRef>(null);
	const refModalGantt = useRef<IProjectModalGanttRef>(null);

	const [programs, setPrograms] = useState<IProgram[]>();
	const [myProjects, setMyProjects] = useState(false);

	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);
	const [showTagsFilters, setShowTagsFilters] = useState(false);

	const [layoutList, setLayoutList] = useState<"card" | "list">(getLocalStorage(`ProjectsScreenLayoutList-${user.efa005Id}`) === "card" ? "card" : "list");
	const [showFilters, setShowFilters] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorLoadingGroupers, setErrorLoadingGroupers] = useState(false);

	/** FILTERS */
	const [filters, setFilters] = useState<IFiltersProjectPanel>({ 
		f: "", 
		responsible: "",
		authority: "",
		sector: objQueryParams?.sector ?? "",
		situation: objQueryParams?.situation ?? "",
		startDate: null,
		endDate: null,
		tags: "",
		map: objQueryParams?.map ?? "",
		perspective: objQueryParams?.perspective ?? "",
		objective: objQueryParams?.objective ?? "",
		program: "",
		project: params.projectId ?? "",
		delayed: objQueryParams?.delayed ?? ""
	});
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		control: controlFilters, 
		reset: resetFilters, 
		watch: watchFilters,
		setValue: setValueFilters 
	} = useForm();

	const mapValueWatch = watchFilters("map");
	const perspectiveValueWatch = watchFilters("perspective");

	const submitFilters = async (data: any) => {
		const dataForm = data;
		
		if(typeof data.situation === "object")
			dataForm.situation = data.situation?.value || "";
		if(typeof data.authority === "object")
			dataForm.authority = data.authority?.value || "";
		if(typeof data.responsible === "object")
			dataForm.responsible = data.responsible?.value || "";
		if(typeof data.sector === "object")
			dataForm.sector = data.sector?.value || "";
		if(typeof data.map === "object")
			dataForm.map = data.map?.value || "";
		if(typeof data.perspective === "object")
			dataForm.perspective = data.perspective?.value || "";
		if(typeof data.objective === "object")
			dataForm.objective = data.objective?.value || "";
		if(typeof data.program === "object")
			dataForm.program = data.program?.value || "";
		if(typeof data.project === "object")
			dataForm.project = data.project?.value || "";

		if(myProjects)
			dataForm.responsible = user.efa005Id.toString();

		setFilters(dataForm);
	};
	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response || "";
			return updateState;
		});
	};

	const loadGroupers = async () => {
		try{
			setLoading(true);
			setErrorLoadingGroupers(false);

			if(Array.isArray(filters.situation))
				filters.situation = extractSingleValueFromArrayOfObject("value", filters.situation)?.toString();

			const {data} = await RCCServicesApi.getRequest({
				method: "Program/List",
				urlParameters: {
					f: filters.f || "",
					responsible: filters.responsible || "",
					authority: filters.authority || "",
					sector: filters.sector || "",
					situation: filters.situation || "",
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : "",
					map: filters.map || "",
					perspective: filters.perspective || "",
					objective: filters.objective || "",
					tags: filters.tags || "",
					program: filters.program || "",
					project: filters.project || ""
				}
			});
			setPrograms(data);
			setLoading(false);

			// se tiver parametro expande
			if(filters.project && params.projectId){
				const groupers = document.querySelectorAll(".rccaccordion-groupers:not(.Mui-expanded)");
				const {length} = groupers;

				if(length === 0)
					return;

				for(let i = 0; i < 3; i += 1){
					if(groupers[i] === undefined)
						return;
					const accordionNode = groupers[i].firstChild as HTMLDivElement;
					accordionNode.click();
				}
			}

		}catch(err){
			setErrorLoadingGroupers(true);
			standardErrorMessages(err, translate("Erro ao carregar os agrupadores"), addToast);
			setLoading(false);
		}
	};

	const onCallbackProgram = async (id: number) => {
		const {data} = await RCCServicesApi.getRequest({
			method: "Program/SelectForList",
			urlParameters: {
				id,
				f: filters.f || "",
				responsible: filters.responsible || "",
				authority: filters.authority || "",
				sector: filters.sector || "",
				situation: filters.situation || "",
				startDate: filters.startDate ? formatDate(filters.startDate) : "",
				endDate: filters.endDate ? formatDate(filters.endDate) : "",
				map: filters.map || "",
				perspective: filters.perspective || "",
				objective: filters.objective || "",
				tags: filters.tags || "",
				program: filters.program || "",
				project: filters.project || ""
			}
		});
		changeProgram(data);

	};

	useEffectSkipInitialRender(() => {
		loadGroupers();
	}, [filters]);

	const addProgram = (newProgram: IProgram) => {
		setPrograms( programs ? [...programs, newProgram] : [newProgram]);
	};

	const changeProgram = (editProgram: IProgram) => {
		setPrograms((prevState) => {
			const updateState = prevState?.map((program) => {
			
				if(program.pra001Id === Number(editProgram.pra001Id)){
					return {
						...editProgram
					};
				}
				return program;
			});
			return updateState;
		});

	};


	const changeProject = (editProject: IProject) => {
		setPrograms((prevState) => {
			const updateState = prevState?.map((program) => {
				if(program.pra001Id === Number(editProject.pra002Pra001Id)){
					return {
						...program,
						prtba002: program.prtba002 ? program.prtba002.map((project: IProject) => {
							if(project.pra002Id === Number(editProject.pra002Id)){
								return{
									...project,
									pra002Titulo: editProject.pra002Titulo,
									pra002Efa005Id: editProject.pra002Efa005Id,
									prtba003: editProject.prtba003
								};
							}
							return project;
						}) : []
					};
				}
				return program;
			});
			return updateState;
		});
	};

	const deleteProgram = (removeProgram: IProgram) => {
		setPrograms((prevState) => prevState?.filter((program) => program.pra001Id !== removeProgram.pra001Id));
	};

	const listOfProgram = useMemo(() => (
		<>
			{
				loading ? (
					<Loader />
				) : (
					<>
						{errorLoadingGroupers ? (
							<RCCAlert 
								title={translate("Atenção")}
								description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
							/>
						): (
							<>
								{programs === undefined || programs.length === 0 ? (
									<RCCAlert 
										title={translate("Atenção")}
										description={translate("Nenhum agrupador encontrado")}
									/>
								): (
									<RCCGrid style={{ width: "inherit"}}>
										{programs?.map((program) => (
											<div key={`container-program-${program.pra001Id}`}>
												<ContainerAccordion>
													<div className="accordion-program">
														<ListProgram
															layoutList={layoutList}
															program={program}
															filters={filters}
															onCallbackProgram={onCallbackProgram}
															openModalAction={refModalsActions.current?.openModalAddOrChange}
															openModalChangeProgram={refModalsProgram.current?.actionButtonChange}
															openModalDeleteProgram={refModalsProgram.current?.actionButtonDelete}
															onCallbackChangeProgram={changeProgram}
															onCallbackDeleteProgram={deleteProgram}
															onCallbackChangeProject={changeProject}
															openModalAddSprint={refModalsSprint.current?.actionButtonAdd}
															openModalChangeSprint={refModalsSprint.current?.actionButtonChange}
															openModalAddProject={refModalsProject.current?.actionButtonAdd}
															openModalChangeProject={refModalsProject.current?.actionButtonChange}
															openModalDeleteProject={refModalsProject.current?.actionButtonDelete}
															openModalDetails={refModalsDetails.current?.openModalDetails}
															openModalReleaseActions={refModalReleaseActions.current?.openModalReleaseActions}
															openModalReorderProjects={refModalsProject.current?.actionButtonReorder}
															openModalGantt={refModalGantt.current?.openModalGantt}
														/>
													</div>
												</ContainerAccordion>
											</div>
										))}
									</RCCGrid>
								)}
							</>
						)}
					</>
				)
			}
		</>
	), [programs, loading, errorLoadingGroupers, layoutList]);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Painel de Projetos")}
					icon={() => <AppIcons name="projects" />}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Novo Programa")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => {refModalsProgram.current?.actionButtonAdd();}}
								size="md"
							/>
						</RCCTooltip>
					</CheckPermission>
					<RCCTooltip
						text={layoutList === "card" ? translate("Alterar para lista") : translate("Alterar para cards")}
						color="grey"
					>
						<RCCButton
							icon={{
								name: layoutList === "card" ? "list" : "table", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => {
								if(layoutList === "card"){
									setLayoutList("list");
									setLocalStorage(`ProjectsScreenLayoutList-${user.efa005Id}`, "list");
									return;
								}
								setLocalStorage(`ProjectsScreenLayoutList-${user.efa005Id}`, "card");
								setLayoutList("card");
							}}
						/>
					</RCCTooltip>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Reordenar Programas")}
							color='grey'
						>
							<RCCButton
								color='grey'
								layout="isRounded"
								size="md"
								onClick={(e) => {e.stopPropagation(); if(modalReorderRef.current) modalReorderRef.current?.openModalReorder();}}
							>
								<RCCGrid
									styleInline="transform: rotate(90deg);"
								>
									<RCCIcon
										name= "exchange" 
										color="#FFF" 
										size={16}
									/>
								</RCCGrid>		
							</RCCButton>
						</RCCTooltip>		
					</CheckPermission>
					<Reports grouper={ReportGroupers.PROJECTS} />
					<RCCTooltip
						text={`${translate("Filtros")} Tags`}
						color={showTagsFilters || filters.tags ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "tags", color: "#FFF", size: 16}}
							color={showTagsFilters || filters.tags ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowTagsFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>		
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || 
							filters.authority ||
							filters.endDate || 
							filters.f || 
							filters.responsible || 
							filters.sector || 
							filters.situation ||
							filters.startDate ||
							filters.map ||
							filters.perspective ||
							filters.objective ||
							filters.program ||
							filters.project 
						 ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters ||
								filters.authority ||
								filters.endDate || 
								filters.f || 
								filters.responsible || 
								filters.sector || 
								filters.situation ||
								filters.startDate ||
								filters.map ||
								filters.perspective ||
								filters.objective ||
								filters.program ||
								filters.project 
							 ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>
				<ButtonExpandGroupers />
				{/* Filters */}
				<FiltersContainer
					showFilters={showFilters}
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						f: "", 
						responsible: { value: "", label: ""},
						authority: { value: "", label: ""},
						sector: { value: "", label: ""},
						situation: { value: "", label: ""},
						map: "",
						perspective: "",
						objective: "",
						program: { value: "", label: ""},
						project: { value: "", label: ""},
						startDate: null,
						endDate: null
					})}
				>
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<DateTimePickerRange
								control={controlFilters}
								label={translate("Período")}
								labelSeparator={translate("até")}
								nameInit = "startDate"
								nameEnd = "endDate"
								defaultValueEnd={filters.endDate}
								defaultValueInit={filters.startDate}
								type="datePicker"
								setValue={setValueFilters}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="responsible"
								label={translate("Responsável")}
								disabled={myProjects}
								defaultValue={filters.responsible}
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="authority"
								label={translate("Autoridade")}
								defaultValue={filters.authority}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovMaps 
								control={controlFilters}
								name="map"
								label={translate("Mapas")}
								defaultValue={filters.map || undefined}
								onChangeCapture={() => {
									setValueFilters("perspective" , undefined);
									setValueFilters("objective" , undefined);
								}}
								submit="value and label"
								cacheOptions={false}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovPerspectives 
							  key={mapValueWatch?.value || mapValueWatch || v4()}
								control={controlFilters}
								name="perspective"
								label={translate("Perspectivas")}
								defaultValue={filters.perspective}
								extraParametersOnLoad={{
									map: mapValueWatch?.value || mapValueWatch || ""
								}}
								onChangeCapture={() => {
									setValueFilters("objective" , undefined);
								}}
								submit="value and label"
								disabled={!mapValueWatch}
								cacheOptions={false}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovObjectives 
								key={perspectiveValueWatch?.value || perspectiveValueWatch || v4()}
								control={controlFilters}
								name="objective"
								label={translate("Objetivo")}
								defaultValue={filters.objective}
								extraParametersOnLoad={{
									map: mapValueWatch?.value || mapValueWatch || "",
									perspective: perspectiveValueWatch?.value || perspectiveValueWatch || ""
								}}
								submit="value and label"
								disabled={!mapValueWatch || !perspectiveValueWatch}
								cacheOptions={false}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovProgram
								control={controlFilters}
								name="program"
								label={translate("Programas")}
								defaultValue={filters.program}
								isClearable
							/>
						</RCCGrid>	
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovProject
								control={controlFilters}
								name="project"
								label={translate("Projetos")}
								defaultValue={filters.project}
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovSectors 
								control={controlFilters}
								name="sector"
								label={translate("Setor")}
								defaultValue={filters.sector}
								isClearable
							/>
						</RCCGrid>			
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<Select 
								control={controlFilters}
								isClearable
								name="situation"
								label={translate("Situação")}
								defaultValue={filters.situation}
								options= {[{
									label: translate("Aberta"),
									value: ProjectSituation.OPEN
								},{
									label: translate("Fechada"),
									value: ProjectSituation.CLOSED
								}]}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="f" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.f}
							/>
						</RCCGrid>
					</RCCGrid>            
				</FiltersContainer>

				{/* Filters Tags */}
				<FiltersTagsContainer 
					ref={filtersTagsRef}
					onSubmitFilters={submitFiltersTags}
					tagType={TagsTypes.PROJECTS}
					showFilters={showTagsFilters}
				/>	

				<form onSubmit={handleSubmitFilters(submitFilters)} style={{marginBottom: "1rem"}}>
					<RCCGrid
						container
						item
						spacing={1}
						justifyContent="flex-end"
					>
						<RCCGrid
							container
							item
							xs={12}
							md={4}
							lg={6}
							justifyContent="flex-end"
						>
							<RCCSelectOptionsCard
								defaultValue={myProjects ? "S" : "N"}
								options={[{
									value: "N",
									label: translate("Todos Projetos")
								},{
									value: "S",
									label: translate("Meus Projetos")
								}]}
								label="&nbsp;"
								onChange={(e) => {
									setValueFilters("responsible", { value: "", label: ""});
									if(e === "S"){
										setMyProjects(true);
										setFilters({
											...filters,
											responsible: user.efa005Id.toString()
										});
										return;
									}
									setMyProjects(false);
									setFilters({
										...filters,
										responsible: ""
									});
								}}
							/>
						</RCCGrid>
					</RCCGrid>
				</form>

				{/* Acordeon - Programs */}
				{listOfProgram}

			</RCCGrid>

			<ModalAddChangeDeleteKanban 
				ref={refModalKanban} 
			/>

			<ModalAddChangeDelete 
				ref={refModalsActions} 
			/>

			<ModalProgram 
				ref={refModalsProgram}
				onCallbackAddProgram={addProgram}
				onCallbackProgram={onCallbackProgram}
			/>

			<ModalProject
				ref={refModalsProject}
				onCallbackProgram={onCallbackProgram}
				loadGroupers={loadGroupers}				
			/>

			<ModalSprint 
				ref={refModalsSprint}
				onCallbackProgram={onCallbackProgram}
			/>

			<ModalDetails 
				ref={refModalsDetails}
			/>

			<ModalReorder 
				ref={modalReorderRef}
				modalName={translate("Reordenar Programas")}
				names={{
					nameForId: "pra001Id",
					nameForOrder: "pra001Ordem",
					nameForTitle: "pra001Titulo"
				}}
				routeToList="Program/ListOrder"
				routeToSave="Program/Reorder"
				onCallbackSave={() => loadGroupers()}
			/>

			<ModalReleaseActions 
				ref={refModalReleaseActions}
			/>

			<ModalGantt 
				ref={refModalGantt}
			/>
		</>
	);
};

export default ProjectsPanel;
